import type { BankCountry, MobileOperator, MoneyRecipient } from '@/types/portal';
import { imageUrl } from '@/utils/assets';

/**
 * Mobile money operators a single payment can settle to, grouped by the
 * settlement currency of the funding wallet that pays them out.
 */
export const mobileOperators: MobileOperator[] = [
  { id: 'op-mpesa-ke', name: 'M-PESA', currency: 'KES', color: '#43B02A', mark: 'M' },
  { id: 'op-airtel-ke', name: 'Airtel Money', currency: 'KES', color: '#E40000', mark: 'A' },
  { id: 'op-mpesa-tz', name: 'M-Pesa', currency: 'TZS', color: '#E40000', mark: 'M' },
  { id: 'op-tigo-tz', name: 'Tigo Pesa', currency: 'TZS', color: '#0067B1', mark: 'T' },
  { id: 'op-airtel-tz', name: 'Airtel Money', currency: 'TZS', color: '#E40000', mark: 'A' },
  { id: 'op-mtn-ug', name: 'MTN MoMo', currency: 'UGX', color: '#FFCC00', mark: 'M' },
  { id: 'op-airtel-ug', name: 'Airtel Money', currency: 'UGX', color: '#E40000', mark: 'A' },
];

/**
 * Numbers this finance user has paid before, plus a handful they keep as
 * favourites. Selecting a row fills the mobile number field in one tap.
 */
export const moneyRecipients: MoneyRecipient[] = [
  {
    id: 'mr-furaha',
    name: 'Furaha Salim',
    avatar: imageUrl('creators/crt-furaha.webp'),
    countryCode: 'KE',
    number: '254712345678',
    operatorId: 'op-mpesa-ke',
    favourite: true,
  },
  {
    id: 'mr-grace',
    name: 'Grace Wambui',
    avatar: imageUrl('creators/crt-grace.webp'),
    countryCode: 'KE',
    number: '254722908877',
    operatorId: 'op-mpesa-ke',
    favourite: true,
  },
  {
    id: 'mr-sanaa',
    name: 'Sanaa Njeri',
    avatar: imageUrl('creators/crt-sanaa.webp'),
    countryCode: 'KE',
    number: '254733114422',
    operatorId: 'op-mpesa-ke',
    favourite: true,
  },
  {
    id: 'mr-zawadi',
    name: 'Zawadi Mwangi',
    avatar: imageUrl('creators/crt-zawadi.webp'),
    countryCode: 'KE',
    number: '254701556677',
    operatorId: 'op-airtel-ke',
    favourite: false,
  },
  {
    id: 'mr-brian',
    name: 'Brian Otieno',
    avatar: imageUrl('creators/crt-brian.webp'),
    countryCode: 'KE',
    number: '254790112233',
    operatorId: 'op-mpesa-ke',
    favourite: false,
  },
  {
    id: 'mr-neema',
    name: 'Neema Achieng',
    avatar: imageUrl('creators/crt-neema.webp'),
    countryCode: 'KE',
    number: '254799001122',
    operatorId: 'op-mpesa-ke',
    favourite: false,
  },
  {
    id: 'mr-peter',
    name: 'Peter Njenga Kironji',
    avatar: imageUrl('payments/mr-peter-njenga.webp'),
    countryCode: 'KE',
    number: '254799402657',
    operatorId: 'op-mpesa-ke',
    favourite: true,
  },
  {
    id: 'mr-david',
    name: 'David Kariuki',
    avatar: imageUrl('payments/mr-david-kariuki.webp'),
    countryCode: 'KE',
    number: '254711556677',
    operatorId: 'op-mpesa-ke',
    favourite: false,
  },
];

/**
 * Bank markets the portal can send to, each with the banks it settles in.
 * The list spans Africa, Europe, the Americas, the Middle East and Asia so a
 * single payment can reach a bank almost anywhere in the world.
 */
export const bankCountries: BankCountry[] = [
  {
    code: 'KE',
    name: 'Kenya',
    flag: '🇰🇪',
    currency: 'KES',
    dialCode: '+254',
    banks: [
      'KCB Bank',
      'Equity Bank',
      'Co-operative Bank',
      'Absa Bank Kenya',
      'NCBA Bank',
      'Family Bank',
      'Stanbic Bank Kenya',
      'Standard Chartered Kenya',
      'Diamond Trust Bank',
      'I&M Bank',
    ],
  },
  {
    code: 'TZ',
    name: 'Tanzania',
    flag: '🇹🇿',
    currency: 'TZS',
    dialCode: '+255',
    banks: [
      'CRDB Bank',
      'NMB Bank',
      'NBC Bank',
      'Exim Bank Tanzania',
      'Stanbic Bank Tanzania',
      'Absa Bank Tanzania',
    ],
  },
  {
    code: 'UG',
    name: 'Uganda',
    flag: '🇺🇬',
    currency: 'UGX',
    dialCode: '+256',
    banks: [
      'Stanbic Bank Uganda',
      'Centenary Bank',
      'DFCU Bank',
      'Absa Bank Uganda',
      'Housing Finance Bank',
    ],
  },
  {
    code: 'RW',
    name: 'Rwanda',
    flag: '🇷🇼',
    currency: 'RWF',
    dialCode: '+250',
    banks: ['Bank of Kigali', 'I&M Bank Rwanda', 'Equity Bank Rwanda', 'Cogebanque'],
  },
  {
    code: 'NG',
    name: 'Nigeria',
    flag: '🇳🇬',
    currency: 'NGN',
    dialCode: '+234',
    banks: [
      'GTBank',
      'Access Bank',
      'Zenith Bank',
      'First Bank of Nigeria',
      'United Bank for Africa',
      'Fidelity Bank',
    ],
  },
  {
    code: 'GH',
    name: 'Ghana',
    flag: '🇬🇭',
    currency: 'GHS',
    dialCode: '+233',
    banks: ['GCB Bank', 'Ecobank Ghana', 'Fidelity Bank Ghana', 'Absa Bank Ghana', 'Stanbic Bank Ghana'],
  },
  {
    code: 'ZA',
    name: 'South Africa',
    flag: '🇿🇦',
    currency: 'ZAR',
    dialCode: '+27',
    banks: ['Standard Bank', 'FNB', 'Absa', 'Nedbank', 'Capitec Bank', 'Investec'],
  },
  {
    code: 'EG',
    name: 'Egypt',
    flag: '🇪🇬',
    currency: 'EGP',
    dialCode: '+20',
    banks: [
      'National Bank of Egypt',
      'Banque Misr',
      'Commercial International Bank',
      'QNB Alahli',
    ],
  },
  {
    code: 'MA',
    name: 'Morocco',
    flag: '🇲🇦',
    currency: 'MAD',
    dialCode: '+212',
    banks: ['Attijariwafa Bank', 'Banque Populaire', 'Bank of Africa', 'Société Générale Maroc'],
  },
  {
    code: 'SN',
    name: 'Senegal',
    flag: '🇸🇳',
    currency: 'XOF',
    dialCode: '+221',
    banks: ['Société Générale Sénégal', 'Ecobank Sénégal', 'BICIS', 'Banque Atlantique'],
  },
  {
    code: 'CI',
    name: "Côte d'Ivoire",
    flag: '🇨🇮',
    currency: 'XOF',
    dialCode: '+225',
    banks: ['Société Générale Côte d’Ivoire', 'Ecobank CI', 'NSIA Banque', 'Banque Atlantique'],
  },
  {
    code: 'ET',
    name: 'Ethiopia',
    flag: '🇪🇹',
    currency: 'ETB',
    dialCode: '+251',
    banks: ['Commercial Bank of Ethiopia', 'Awash Bank', 'Dashen Bank', 'Bank of Abyssinia'],
  },
  {
    code: 'ZM',
    name: 'Zambia',
    flag: '🇿🇲',
    currency: 'ZMW',
    dialCode: '+260',
    banks: ['Zanaco', 'Absa Bank Zambia', 'Stanbic Bank Zambia', 'Zambia National Commercial Bank'],
  },
  {
    code: 'ZW',
    name: 'Zimbabwe',
    flag: '🇿🇼',
    currency: 'ZWL',
    dialCode: '+263',
    banks: ['CBZ Bank', 'Steward Bank', 'FBC Bank', 'Stanbic Bank Zimbabwe'],
  },
  {
    code: 'MZ',
    name: 'Mozambique',
    flag: '🇲🇿',
    currency: 'MZN',
    dialCode: '+258',
    banks: ['Millennium bim', 'Standard Bank Mozambique', 'Absa Bank Mozambique'],
  },
  {
    code: 'CM',
    name: 'Cameroon',
    flag: '🇨🇲',
    currency: 'XAF',
    dialCode: '+237',
    banks: ['Afriland First Bank', 'Société Générale Cameroun', 'BICEC'],
  },
  {
    code: 'CD',
    name: 'Congo (Democratic Rep.)',
    flag: '🇨🇩',
    currency: 'CDF',
    dialCode: '+243',
    banks: ['Rawbank', 'Equity BCDC', 'Trust Merchant Bank'],
  },
  {
    code: 'SS',
    name: 'South Sudan',
    flag: '🇸🇸',
    currency: 'SSP',
    dialCode: '+211',
    banks: ['Equity Bank South Sudan', 'Ecobank South Sudan', 'Nile Commercial Bank'],
  },
  {
    code: 'GB',
    name: 'United Kingdom',
    flag: '🇬🇧',
    currency: 'GBP',
    dialCode: '+44',
    banks: [
      'Barclays',
      'HSBC UK',
      'Lloyds Bank',
      'NatWest',
      'Santander UK',
      'Monzo',
      'Starling Bank',
      'Revolut',
    ],
  },
  {
    code: 'IE',
    name: 'Ireland',
    flag: '🇮🇪',
    currency: 'EUR',
    dialCode: '+353',
    banks: ['Bank of Ireland', 'AIB', 'Ulster Bank', 'Revolut'],
  },
  {
    code: 'DE',
    name: 'Germany',
    flag: '🇩🇪',
    currency: 'EUR',
    dialCode: '+49',
    banks: ['Deutsche Bank', 'Commerzbank', 'N26', 'DKB', 'ING Germany'],
  },
  {
    code: 'FR',
    name: 'France',
    flag: '🇫🇷',
    currency: 'EUR',
    dialCode: '+33',
    banks: ['BNP Paribas', 'Société Générale', 'Crédit Agricole', 'La Banque Postale'],
  },
  {
    code: 'NL',
    name: 'Netherlands',
    flag: '🇳🇱',
    currency: 'EUR',
    dialCode: '+31',
    banks: ['ING', 'Rabobank', 'ABN AMRO', 'bunq'],
  },
  {
    code: 'ES',
    name: 'Spain',
    flag: '🇪🇸',
    currency: 'EUR',
    dialCode: '+34',
    banks: ['Banco Santander', 'BBVA', 'CaixaBank', 'Bankinter'],
  },
  {
    code: 'IT',
    name: 'Italy',
    flag: '🇮🇹',
    currency: 'EUR',
    dialCode: '+39',
    banks: ['Intesa Sanpaolo', 'UniCredit', 'Banco BPM', 'BPER Banca'],
  },
  {
    code: 'PT',
    name: 'Portugal',
    flag: '🇵🇹',
    currency: 'EUR',
    dialCode: '+351',
    banks: ['Millennium BCP', 'Caixa Geral de Depósitos', 'Novo Banco'],
  },
  {
    code: 'CH',
    name: 'Switzerland',
    flag: '🇨🇭',
    currency: 'CHF',
    dialCode: '+41',
    banks: ['UBS', 'Raiffeisen', 'PostFinance', 'Julius Baer'],
  },
  {
    code: 'SE',
    name: 'Sweden',
    flag: '🇸🇪',
    currency: 'SEK',
    dialCode: '+46',
    banks: ['Swedbank', 'Handelsbanken', 'SEB', 'Nordea'],
  },
  {
    code: 'NO',
    name: 'Norway',
    flag: '🇳🇴',
    currency: 'NOK',
    dialCode: '+47',
    banks: ['DNB', 'Nordea Norway', 'SpareBank 1', 'Danske Bank'],
  },
  {
    code: 'DK',
    name: 'Denmark',
    flag: '🇩🇰',
    currency: 'DKK',
    dialCode: '+45',
    banks: ['Danske Bank', 'Nykredit', 'Nordea Denmark', 'Jyske Bank'],
  },
  {
    code: 'PL',
    name: 'Poland',
    flag: '🇵🇱',
    currency: 'PLN',
    dialCode: '+48',
    banks: ['PKO Bank Polski', 'Bank Pekao', 'mBank', 'Santander Bank Polska'],
  },
  {
    code: 'US',
    name: 'United States',
    flag: '🇺🇸',
    currency: 'USD',
    dialCode: '+1',
    banks: [
      'Chase',
      'Bank of America',
      'Wells Fargo',
      'Citibank',
      'Capital One',
      'US Bank',
      'PNC Bank',
    ],
  },
  {
    code: 'CA',
    name: 'Canada',
    flag: '🇨🇦',
    currency: 'CAD',
    dialCode: '+1',
    banks: ['RBC Royal Bank', 'TD Canada Trust', 'Scotiabank', 'BMO', 'CIBC'],
  },
  {
    code: 'BR',
    name: 'Brazil',
    flag: '🇧🇷',
    currency: 'BRL',
    dialCode: '+55',
    banks: ['Banco do Brasil', 'Itaú Unibanco', 'Bradesco', 'Santander Brasil'],
  },
  {
    code: 'MX',
    name: 'Mexico',
    flag: '🇲🇽',
    currency: 'MXN',
    dialCode: '+52',
    banks: ['BBVA México', 'Banorte', 'Santander México', 'Citibanamex'],
  },
  {
    code: 'AR',
    name: 'Argentina',
    flag: '🇦🇷',
    currency: 'ARS',
    dialCode: '+54',
    banks: ['Banco Nación', 'Banco Galicia', 'Santander Argentina'],
  },
  {
    code: 'AE',
    name: 'United Arab Emirates',
    flag: '🇦🇪',
    currency: 'AED',
    dialCode: '+971',
    banks: ['Emirates NBD', 'First Abu Dhabi Bank', 'Mashreq Bank', 'Abu Dhabi Commercial Bank'],
  },
  {
    code: 'SA',
    name: 'Saudi Arabia',
    flag: '🇸🇦',
    currency: 'SAR',
    dialCode: '+966',
    banks: ['Al Rajhi Bank', 'Saudi National Bank', 'Riyad Bank', 'SABB'],
  },
  {
    code: 'QA',
    name: 'Qatar',
    flag: '🇶🇦',
    currency: 'QAR',
    dialCode: '+974',
    banks: ['Qatar National Bank', 'Doha Bank', 'Commercial Bank of Qatar'],
  },
  {
    code: 'TR',
    name: 'Turkey',
    flag: '🇹🇷',
    currency: 'TRY',
    dialCode: '+90',
    banks: ['Ziraat Bank', 'İş Bankası', 'Garanti BBVA', 'Yapı Kredi'],
  },
  {
    code: 'IN',
    name: 'India',
    flag: '🇮🇳',
    currency: 'INR',
    dialCode: '+91',
    banks: ['HDFC Bank', 'ICICI Bank', 'State Bank of India', 'Axis Bank', 'Kotak Mahindra Bank'],
  },
  {
    code: 'PK',
    name: 'Pakistan',
    flag: '🇵🇰',
    currency: 'PKR',
    dialCode: '+92',
    banks: ['HBL', 'Meezan Bank', 'UBL', 'Bank Alfalah'],
  },
  {
    code: 'BD',
    name: 'Bangladesh',
    flag: '🇧🇩',
    currency: 'BDT',
    dialCode: '+880',
    banks: ['BRAC Bank', 'Dutch-Bangla Bank', 'City Bank', 'Eastern Bank'],
  },
  {
    code: 'CN',
    name: 'China',
    flag: '🇨🇳',
    currency: 'CNY',
    dialCode: '+86',
    banks: ['ICBC', 'China Construction Bank', 'Bank of China', 'Agricultural Bank of China'],
  },
  {
    code: 'JP',
    name: 'Japan',
    flag: '🇯🇵',
    currency: 'JPY',
    dialCode: '+81',
    banks: ['MUFG Bank', 'SMBC', 'Mizuho Bank', 'Japan Post Bank'],
  },
  {
    code: 'SG',
    name: 'Singapore',
    flag: '🇸🇬',
    currency: 'SGD',
    dialCode: '+65',
    banks: ['DBS Bank', 'OCBC Bank', 'UOB', 'Standard Chartered Singapore'],
  },
  {
    code: 'AU',
    name: 'Australia',
    flag: '🇦🇺',
    currency: 'AUD',
    dialCode: '+61',
    banks: ['Commonwealth Bank', 'Westpac', 'ANZ', 'NAB', 'Macquarie Bank'],
  },
  {
    code: 'NZ',
    name: 'New Zealand',
    flag: '🇳🇿',
    currency: 'NZD',
    dialCode: '+64',
    banks: ['ANZ New Zealand', 'ASB Bank', 'BNZ', 'Westpac NZ'],
  },
  {
    code: 'AL',
    name: 'Albania',
    flag: '🇦🇱',
    currency: 'ALL',
    dialCode: '+355',
    banks: ['Banka Kombëtare Tregtare', 'Raiffeisen Bank Albania', 'Credins Bank'],
  },
  {
    code: 'DZ',
    name: 'Algeria',
    flag: '🇩🇿',
    currency: 'DZD',
    dialCode: '+213',
    banks: ['BNA', 'BEA', 'Société Générale Algérie'],
  },
  {
    code: 'AF',
    name: 'Afghanistan',
    flag: '🇦🇫',
    currency: 'AFN',
    dialCode: '+93',
    banks: ['Afghanistan International Bank', 'Azizi Bank', 'New Kabul Bank'],
  },
  {
    code: 'AX',
    name: 'Åland Islands',
    flag: '🇦🇽',
    currency: 'EUR',
    dialCode: '+358',
    banks: ['Ålandsbanken', 'Nordea'],
  },
];